import Box from "@mui/material/Box/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import ImageService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/ImageService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { useBadgeList } from "../hooks/useBadgeList";
import Badge from "../models/Badge";
import { AppDialog } from "../components/AppDialog";
import { AppToolbar } from "../components/AppToolbar";
import OpsAvatarComponent from "../components/OpsAvatarComponent";
import AddIcon from "@mui/icons-material/Add";
import NoPhotographyIcon from "@mui/icons-material/NoPhotography";
import BadgeService from "../services/BadgeService";
import BadgeCreateDialog from "../components/BadgeSection/BadgeCreateDialog";
import { ROWS_PER_PAGE } from "../App";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CancelIcon from "@mui/icons-material/Cancel";
import BadgeViewDialog from "../components/BadgeSection/BadgeViewDialog";
import BadgeEditDialog from "../components/BadgeSection/BadgeEditDialog";
import { randomId } from "../utils/FileUtils";
import { useLocation } from "react-router-dom";
import useUpdateUrl from "../hooks/useUpdateUrl";
import { Routes } from "../types/Routes";

export default function BadgeList() {
  const { t } = useTranslation();

  const [name, setName] = useState<string>("");
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [update, setUpdate] = useState<number>();
  const { badgeActions, badgeItems } = useBadgeList();

  const searchParams = new URLSearchParams(useLocation().search);
  const titleFilter = searchParams.get("title");
  const {updateUrl} = useUpdateUrl(Routes.BADGES, searchParams);

  useEffect(() => {
    if (titleFilter !== null && titleFilter !== "") {
      setName(titleFilter);
    }

    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBadgerStatusChange = async () => {
    if (!badgeItems.selectedBadge) return;

    if (badgeItems.selectedBadge.externalId) {
      BadgeService.changeBadgeStatus(badgeItems.selectedBadge.externalId, !badgeItems.selectedBadge.enabled)
        .then((response) => {
          if (response.isSuccess()) {
            badgeActions.setSelectedBadge(undefined);
            setUpdate(Date.now());
            NotificationService.info("Badge updated!");
          } else {
            NotificationService.error(response.getMessage());
          }
        })
        .catch((error) => {
          NotificationService.error("Error trying to Enable/Disable Badge.");
        });
    }
  };

  const handleOnUpdate = async (instance: Badge) => {
    (await BadgeService.update(instance)).getContentOrNotifyError((badge) => {
      NotificationService.info(`Badge ${badge.name} updated successfully`);
    });
  };

  const onCloseSiteCreateDialog = () => {
    setOpenCreateModal(false);
    setSelectedFile(undefined);
  };

  const onCloseBadgeEditDialog = () => {
    badgeActions.setEditBadge(undefined);
    badgeActions.setEditMode(false);
    setSelectedFile(undefined);
  };

  const handleImageDelete = () => {
    setSelectedFile("");
  };

  const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    const fileList = e.target.files;

    if (!fileList) return;

    let fileToUpload: File = fileList[0];
    const filename: string = `ensolvers-ops-site-image_${randomId()}`;
    ImageService.uploadImage(fileToUpload, filename, undefined)
      .then((response) => {
        if (response.succeeded()) {
          const iconUrl = response.getContent().url;

          setSelectedFile(iconUrl);
          setIsLoading(false);
        }
      })
      .catch((e) => {
        NotificationService.error("This file can not be uploaded. Please try with a different one");
        setIsLoading(false);
      });
    setSelectedFile("");
  };

  const handleOnCreateBadge = async (badge: Badge) => {
    setIsLoading(true);
    (await BadgeService.create(badge)).getContentOrNotifyError((badge) => {
      NotificationService.info(`Badge ${badge.name} created successfully`);
    });
    setIsLoading(false);
    setSelectedFile("");
  };

  // const status = badgeItems.selectedBadge?.enabled ? "Disable Badge" : "Enable Badge";

  return (
    <>
      <AppToolbar title={t("Badges")}>
        <Box sx={{ display: "flex", alignItems: "flex-start", width: "100%", padding: "5px" }}>
          <TextField
            label="Badge title"
            name="name"
            style={{ maxWidth: "250px" }}
            variant="outlined"
            value={name}
            size="small"
            onChange={(e: any) => {
              setName(e.target.value);
              searchParams.set("title", e.target.value);
              updateUrl();
            }}
          />
        </Box>
      </AppToolbar>

      <TableBasedCrud<Badge>
        elementName={"Badge"}
        disableAutoFetch
        delayFetch={name}
        rowsPerPage={ROWS_PER_PAGE}
        denseTable
        forceUpdateOn={update}
        setIsEditModeActive={badgeActions.setEditMode}
        onDataFetch={(pageNumber, count) =>
          BadgeService.getBadgeFilter(pageNumber, count, name)
            .then((result) => result.getContentOrThrowError())
            .finally(() => setIsLoading(false))
        }
        validateBeforeClosing
        additionalToolbarButtons={() => (
          <Button
            startIcon={<AddIcon />}
            sx={{ color: "black" }}
            onClick={() => {
              setOpenCreateModal(true);
            }}
          >
            Add New Badge
          </Button>
        )}
        onViewMode={(instance) => badgeActions.handleOpenViewDialog(instance)}
        renderDialogContent={(register, errors, control) => <></>}
        resetValue={undefined}
        tableColumns={[
          {
            header: t("Icon"),
            content: (instance) => (
              <>
                <OpsAvatarComponent
                  avatarUrl={instance.iconUrl}
                  forceGenericAvatar={true}
                  genericAvatar={<NoPhotographyIcon />}
                  sx={{ width: "2.5em", height: "2.5em" }}
                />
              </>
            ),
          },
          {
            header: t("Title"),
            content: (instance) => <>{instance.name}</>,
          },
          {
            header: t("Description"),
            content: (instance) => (
              <Box sx={{ maxWidth: "30rem", flexWrap: "wrap", overflowWrap: "break-word" }}>{instance.description}</Box>
            ),
          },
          {
            header: t("Automatic approval"),
            content: (instance) => (
              <>{instance.automaticApproval === true ? <CheckBoxIcon color="success" /> : <CancelIcon color="warning" />}</>
            ),
          },
          {
            header: t("Level Option"),
            content: (instance) => <>{instance.level === true ? <CheckBoxIcon color="success" /> : <CancelIcon color="warning" />}</>,
          },
          {
            header: t("Status"),
            content: (instance) => (
              <Button
                variant="outlined"
                disabled
                style={{
                  textTransform: "none",
                  color: "white",
                  backgroundColor: instance.enabled ? "green" : "orange",
                  maxWidth: "150px",
                }}
                fullWidth
              >
                {instance.enabled ? "Enabled" : "Disabled"}
              </Button>
            ),
          },
        ]}
        buildAdditionalActions={(badge, refresh) => {
          return [
            {
              title: t(badge.enabled ? "Disable" : "Enable"),
              action: () => badgeActions.setSelectedBadge(badge),
            },
            {
              title: "Edit",
              action: () => badgeActions.handleOpenEditDialog(badge),
            },
          ];
        }}
      />

      {/* Dialog to create/view Badge */}
      <BadgeCreateDialog
        onClose={onCloseSiteCreateDialog}
        isLoading={isLoading}
        open={openCreateModal}
        handleUploadImage={handleUploadImage}
        selectedFile={selectedFile ? selectedFile : ""}
        handleOnCreateBadge={handleOnCreateBadge}
        handleImageDelete={handleImageDelete}
      />

      {/* Dialog to view site */}
      {badgeItems.viewBadge && (
        <BadgeViewDialog viewBadge={badgeItems.viewBadge} onClose={() => badgeActions.setViewBadge(undefined)} isLoading={false} />
      )}

      {/* Dialog to edit site */}
      <BadgeEditDialog
        onClose={onCloseBadgeEditDialog}
        handleOnUpdate={handleOnUpdate}
        open={!!badgeItems.editMode}
        isLoading={isLoading}
        handleImageDelete={handleImageDelete}
        handleUploadImage={handleUploadImage}
        selectedFile={selectedFile!}
        editBadge={badgeItems.editBadge!}
      />

      <AppDialog
        open={!!badgeItems.selectedBadge && !badgeItems.editMode}
        onClose={() => badgeActions.setSelectedBadge(undefined)}
        title={badgeItems.selectedBadge?.enabled ? "Disable" : "Enable"}
        content={
          <>
            <Typography>{`The next user is going to be ${badgeItems.selectedBadge?.enabled ? "disabled" : "enabled"}`}</Typography>
            <Typography>Do you want to confirm this action?</Typography>
          </>
        }
        buttons={[
          { label: `Confirm ${badgeItems.selectedBadge?.enabled ? "Disable" : "Enable"}`, onClick: () => handleBadgerStatusChange() },
        ]}
      />
    </>
  );
}
